import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Heading,
  Text,
  Box,
  Stack,
  useToast,
  Input,
  Flex,
} from '@chakra-ui/react';
import CustomCheckboxGroup from '../common/CustomCheckboxGroup';
import { MeContext } from '../../context/MeContext';
import {
  CustomReactSelect,
  formatOptionLabelFilterEntity,
} from '../../components/common/CustomReactSelect';
import CustomToast from '../../common/CustomToast';
import { useContext, useEffect, useState } from 'react';
import { HttpContext } from '../../context/HttpContext';
import sentry from '../../sentry';
import { CustomSendIcon } from '../../theme/icons/CustomSendIcon';
import * as yup from 'yup';
import { ROLES } from '../../constants/roles';
import useRolesList from '../../hooks/useRolesList';
import useEntityManagement from '../../hooks/useEntitiesList';
import { AuthContext } from '../../context/AuthContext';

const SendInvitationModal = (props) => {
  const { authAxios } = useContext(HttpContext);
  const authContext = useContext(AuthContext);
  const meCtx = useContext(MeContext);
  const { fetchData } = meCtx;
  const { account, entities } = meCtx.state;
  const inviter = account;
  const inviterRole = inviter.role;
  const toast = useToast();
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [inviteSent, setInviteSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteeRole, setInviteeRole] = useState(
    inviterRole === ROLES.org_admin.value
      ? ROLES.org_admin.value
      : ROLES.ent_admin.value
  );
  const [inviteeEntity, setInviteeEntity] = useState(
    inviterRole === ROLES.org_admin.value ? null : inviter.entity
  );
  const [emailError, setEmailError] = useState('');
  const [entityError, setEntityError] = useState('');
  const rolesList = useRolesList(inviterRole, entities.length, true);
  const filterAllEntitiesOption = inviteeRole === ROLES.ent_admin.value;
  const entitiesList = useEntityManagement(entities, filterAllEntitiesOption);

  const emailSchema = yup.object().shape({
    email: yup
      .string()
      .email('Please enter a valid email address')
      .required('Email is required'),
  });

  const validateEmail = async (email) => {
    try {
      await emailSchema.validate({ email });
      setEmailError('');
      return true;
    } catch (err) {
      setEmailError(err.message);
      return false;
    }
  };

  const validateEntity = async (entity) => {
    if (inviteeRole === ROLES.ent_admin.value && !entity) {
      setEntityError('Please select an entity');
      return false;
    }
    setEntityError('');
    return true;
  };

  const onEmailChange = async (e) => {
    const email = e.target.value;
    setInviteeEmail(email);
    await validateEmail(email);
  };

  const onEntityChange = async (entity) => {
    setInviteeEntity(entity);
    await validateEntity(entity);
  };

  const handleRoleChange = (role) => {
    setInviteeRole((prev) => (prev === role ? prev : role));
    if (role === ROLES.org_admin.value) {
      setInviteeEntity(null);
    }
  };

  const onSubmit = async () => {
    const isEmailValid = await validateEmail(inviteeEmail);
    if (!isEmailValid) {
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Invalid Email"
            description={emailError}
            onClose={props.onClose}
          />
        ),
      });
      return;
    }

    const isEntityValid = await validateEntity(inviteeEntity);
    if (!isEntityValid) {
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="No Entity Selected"
            description={entityError}
            onClose={props.onClose}
          />
        ),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await authAxios.post(
        `accounts/team_member_invitation`,
        {
          invitation: {
            email: inviteeEmail,
            role: inviteeRole,
            entity_id: inviteeEntity?.value?.id,
            company_name: inviteeEntity?.value?.name,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${authContext.getAccessToken()}`,
          },
        }
      );
      setInviteSent(true);
      props.onClose();
      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Invitation sent."
            description="You have successfully sent an invitation."
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
    } catch (onError) {
      sentry.sendError(onError);
      const errors = onError.response?.data?.errors || [
        'An error occurred during invitation',
      ];
      errors.forEach((error) => {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title={error}
              description=""
              onClose={props.onClose}
            />
          ),
        });
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (inviteSent) {
      fetchData({ inviteSent: true });
    }
    setInviteSent(false);
  }, [fetchData, inviteSent]);

  return (
    <Modal onClose={props.onClose} isOpen={props.isOpen} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex direction="column" align="center" gap={3} pt={3}>
            <Box mb={2}>
              <CustomSendIcon color="#EC675A" width="50px" height="50px" />
            </Box>
            <Heading fontSize={{ base: '24px', lg: '32px' }} lineHeight="130%">
              New invitation
            </Heading>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing="20px" alignItems="flex-start">
            <Text textAlign="center" fontSize="18px" px={2}>
              Enter the email of the person you want to invite to your team.
            </Text>
            <Box w="full">
              <Input
                variant="filled"
                id="inviteeEmail"
                type="text"
                placeholder="Enter email"
                w="full"
                onChange={onEmailChange}
                mb={2}
                isInvalid={!!emailError}
              />
              {emailError && (
                <Text pl={4} color="red.500" fontSize="sm">
                  {emailError}
                </Text>
              )}
            </Box>
          </VStack>
          {inviterRole === ROLES.org_admin.value && (
            <>
              <VStack spacing="20px" mt={6} mb={4}>
                <Text textAlign="center" fontSize="18px" px={2}>
                  Choose role of the person you want to invite to your team.
                </Text>
                <Box w="full">
                  <CustomCheckboxGroup
                    options={rolesList}
                    onChange={(role) => handleRoleChange(role)}
                    defaultChecked={inviteeRole}
                  />
                </Box>
              </VStack>
              {inviteeRole !== ROLES.org_admin.value && entities.length > 1 && (
                <VStack spacing="20px" mt={6} mb={4}>
                  <Text textAlign="left" fontSize="18px" px={2}>
                    Choose the entity you want to invite the person to.
                  </Text>
                  <Box w="full">
                    <CustomReactSelect
                      options={entitiesList}
                      formatOptionLabel={formatOptionLabelFilterEntity}
                      onChange={(entity) => {
                        onEntityChange(entity);
                      }}
                      value={inviteeEntity}
                      placeholder="Select entity"
                      id="entity"
                      isInvalid={!!entityError}
                    />
                    {entityError && (
                      <Text ml={6} pl={4} color="red.500" fontSize="sm">
                        {entityError}
                      </Text>
                    )}
                  </Box>
                </VStack>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Stack
            spacing="10px"
            direction={{ base: 'column', sm: 'row' }}
            width={{ base: 'full', sm: 'auto' }}
          >
            <Button
              onClick={props.onClose}
              alt="Cancel"
              variant="secondary"
              width={{ base: 'full', sm: 'auto' }}
            >
              <Text>Cancel</Text>
            </Button>
            <Button
              alt="Send"
              width={{ base: 'full', sm: 'auto' }}
              onClick={onSubmit}
              isDisabled={!!emailError || isSubmitting}
              isLoading={isSubmitting}
            >
              Send
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SendInvitationModal;
